/**
 * AgentsBrowser — list + create + edit AgentSpec.
 *
 * Layout (lemon-agent inspired):
 *   1. Toolbar row: count badge, search, Refresh, New agent.
 *   2. Card grid: each `AgentCard` shows avatar / status dot / name /
 *      one-line system-prompt blurb / runtime+model+KB+channel badges.
 *      Hover reveals quick "open in /chat" + Archive icons.
 *   3. Right-side `Sheet` drawer with tabs:
 *        - Setup         : profile + LLM spec.
 *        - Capabilities  : KB, skills, tools, MCP, memory, guardrails.
 *        - Versions : `/v1/agents/{id}/versions` history
 *        - Channels : team-wide `/v1/channels/bindings`, filtered by agent
 *        - Runs     : `/v1/activity/sessions?agent_id=...` last 20
 *      Clicking the Edit icon swaps the Setup tab from view to a save
 *      form. Save PATCHes /v1/agents/{id}, refreshes the list, exits
 *      edit mode — no detached Dialog.
 *   4. Quick-create dialog: pick a team **model** from `/llm`, name, prompt, optional KBs.
 *
 * Mutating actions stay gated behind `isAdmin` to match the gateway
 * server-side checks. We fetch `/v1/channels/bindings` once at mount
 * and slice it per-agent so the card grid + drawer share one HTTP hop.
 */

import {
  BookOpen,
  Bot,
  Loader2,
  MessageSquare,
  Plus,
  RefreshCw,
  RotateCcw,
  Search,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { LetterAvatar } from "@/components/chat/avatar";
import { EmptyState, ErrorBanner } from "@/components/empty-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { modelFromLlmConfigSummary, readLlmConfigListItems } from "@/lib/agent-llm-preset";
import { api } from "@/lib/api";
import { setChatPreselectedAgentId } from "@/lib/chat-preselect";
import {
  type AgentListResponse,
  type AgentSpec,
  type AgentSummary,
  type ChannelBindingListResponse,
  type ChannelBindingSummary,
  type CreateAgentRequest,
  DEFAULT_AGENT_SPEC,
  type KbView,
  type LLMConfigListResponse,
  type LLMConfigSummary,
} from "@/lib/types";
import { cn } from "@/lib/utils";

import { AgentDrawer } from "./agent-drawer";

interface AgentsBrowserProps {
  isAdmin: boolean;
  kbs: KbView[];
}

export function AgentsBrowser({ isAdmin, kbs }: AgentsBrowserProps) {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentSummary[] | null>(null);
  const [bindings, setBindings] = useState<ChannelBindingSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [archiveTarget, setArchiveTarget] = useState<AgentSummary | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [filter, setFilter] = useState("");
  // When on, the API call asks the gateway for archived rows too. We keep
  // this off by default because the dashboard's primary job is "what's
  // running" — archived agents are a recovery / audit affordance.
  const [includeArchived, setIncludeArchived] = useState(false);

  const refreshAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api<AgentListResponse>(
        `/agents?limit=200${includeArchived ? "&include_archived=true" : ""}`,
      );
      setAgents(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  const refreshBindings = useCallback(async () => {
    try {
      const res = await api<ChannelBindingListResponse>("/channels/bindings");
      setBindings(res.items);
    } catch {
      // Channels are optional polish — a 403 (non-admin can list) or a
      // 5xx shouldn't blank the agents grid. We just leave the
      // channel column empty.
      setBindings([]);
    }
  }, []);

  useEffect(() => {
    void refreshAgents();
    void refreshBindings();
  }, [refreshAgents, refreshBindings]);

  const kbsById = useMemo(() => {
    const m = new Map<string, KbView>();
    for (const kb of kbs) m.set(kb.id, kb);
    return m;
  }, [kbs]);

  // Pre-bucket bindings by agent_id so each card / the drawer get O(1)
  // lookups instead of re-filtering the full list on every render.
  const bindingsByAgentId = useMemo(() => {
    const m = new Map<string, ChannelBindingSummary[]>();
    for (const b of bindings) {
      const arr = m.get(b.agent_id);
      if (arr) arr.push(b);
      else m.set(b.agent_id, [b]);
    }
    return m;
  }, [bindings]);

  const filteredAgents = useMemo(() => {
    if (!agents) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) => {
      const spec = a.spec ?? DEFAULT_AGENT_SPEC;
      const channels = bindingsByAgentId.get(a.id) ?? [];
      const hay = [
        a.name,
        a.description ?? "",
        a.id,
        spec.runtime,
        spec.model.model,
        spec.model.provider,
        ...channels.map((c) => `${c.kind}:${c.slug}`),
      ]
        .join(" ")
        .toLowerCase();
      return hay.includes(q);
    });
  }, [agents, bindingsByAgentId, filter]);

  const selectedAgent = useMemo(
    () => (selected ? (agents?.find((a) => a.id === selected) ?? null) : null),
    [agents, selected],
  );

  const openInChat = useCallback(
    (id: string) => {
      setChatPreselectedAgentId(id);
      navigate("/chat");
    },
    [navigate],
  );

  const handleArchive = useCallback(async () => {
    if (!archiveTarget) return;
    setArchiveBusy(true);
    try {
      await api(`/agents/${archiveTarget.id}`, { method: "DELETE" });
      toast.success(`Archived ${archiveTarget.name}`);
      setSelected((prev) => (prev === archiveTarget.id ? null : prev));
      setArchiveTarget(null);
      await refreshAgents();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to archive");
    } finally {
      setArchiveBusy(false);
    }
  }, [archiveTarget, refreshAgents]);

  const handleRestore = useCallback(
    async (agent: AgentSummary) => {
      try {
        await api(`/agents/${agent.id}/restore`, { method: "POST" });
        toast.success(`Restored ${agent.name}`);
        await refreshAgents();
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to restore");
      }
    },
    [refreshAgents],
  );

  return (
    <div className="space-y-3">
      {/* ── Toolbar: count + search + actions ─────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="h-6 rounded-md border-border/70 px-2 text-[11px]">
          {agents ? `${agents.length} agent${agents.length === 1 ? "" : "s"}` : "…"}
        </Badge>
        <div className="relative min-w-[180px] max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name, model, runtime, channel…"
            className="h-8 rounded-md border-border/70 bg-card pl-8 text-[12px]"
          />
        </div>
        <div className="ml-auto flex items-center gap-1.5">
          <label className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-md border border-border/70 bg-card px-2 text-[11px] text-muted-foreground hover:bg-muted">
            <input
              type="checkbox"
              className="h-3 w-3 accent-primary"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
            />
            Show archived
          </label>
          <Button
            size="sm"
            variant="ghost"
            className="h-8"
            onClick={() => {
              void refreshAgents();
              void refreshBindings();
            }}
            aria-label="Refresh"
            title="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </Button>
          {isAdmin ? (
            <Button size="sm" className="h-8" onClick={() => setCreating(true)}>
              <Plus className="h-3.5 w-3.5" /> New agent
            </Button>
          ) : null}
        </div>
      </div>

      {error ? <ErrorBanner>{error}</ErrorBanner> : null}

      {/* ── Card grid ─────────────────────────────────────────── */}
      {loading && agents === null ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, idx) => (
            <Card
              key={`agent-skeleton-${
                // biome-ignore lint/suspicious/noArrayIndexKey: stable skeleton placeholder count
                idx
              }`}
              className="space-y-2 p-3.5 shadow-none"
            >
              <Skeleton className="h-5 w-1/2" />
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-2/3" />
            </Card>
          ))}
        </div>
      ) : agents && agents.length === 0 ? (
        <EmptyState className="py-10">
          <Bot className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
          No agents yet. {isAdmin ? "Create one to start chatting." : "Ask an admin to create one."}
        </EmptyState>
      ) : filteredAgents.length === 0 ? (
        <EmptyState className="py-10">No agents match “{filter}”.</EmptyState>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredAgents.map((agent) => (
            <AgentCard
              key={agent.id}
              agent={agent}
              kbsById={kbsById}
              channels={bindingsByAgentId.get(agent.id) ?? []}
              isAdmin={isAdmin}
              onOpen={() => setSelected(agent.id)}
              onChat={() => openInChat(agent.id)}
              onArchive={() => setArchiveTarget(agent)}
              onRestore={() => void handleRestore(agent)}
            />
          ))}
        </div>
      )}

      {/* ── Right-side detail drawer ──────────────────────────── */}
      <Sheet
        open={selected !== null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
      >
        <SheetContent side="right" className="flex w-full flex-col gap-0 p-0" hideClose>
          {selectedAgent ? (
            <AgentDrawer
              agent={selectedAgent}
              kbs={kbs}
              kbsById={kbsById}
              bindings={bindings}
              isAdmin={isAdmin}
              onClose={() => setSelected(null)}
              onChat={() => openInChat(selectedAgent.id)}
              onArchive={() => setArchiveTarget(selectedAgent)}
              onRestore={() => handleRestore(selectedAgent)}
              onChanged={async () => {
                await refreshAgents();
                await refreshBindings();
              }}
            />
          ) : null}
        </SheetContent>
      </Sheet>

      {/* ── Confirm archive dialog ────────────────────────────── */}
      <Dialog
        open={archiveTarget !== null}
        onOpenChange={(open) => {
          if (!open && !archiveBusy) setArchiveTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Archive agent</DialogTitle>
            <DialogDescription>
              {archiveTarget
                ? `Archive “${archiveTarget.name}”? It will be hidden from /chat and from any inbound channels. You can bring it back by toggling “Show archived” and clicking Restore.`
                : ""}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveTarget(null)} disabled={archiveBusy}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleArchive()}
              disabled={archiveBusy}
            >
              {archiveBusy ? <Loader2 className="animate-spin" /> : null}
              Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Quick create dialog ───────────────────────────────── */}
      {creating ? (
        <QuickCreateAgentDialog
          kbs={kbs}
          onClose={() => setCreating(false)}
          onCreated={async (agent) => {
            // Refresh first so `selectedAgent` (derived from `agents`)
            // can resolve the new row immediately — otherwise the
            // drawer flashes empty for the duration of the fetch.
            await refreshAgents();
            setSelected(agent.id);
          }}
        />
      ) : null}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// Agent card (grid tile)
// ────────────────────────────────────────────────────────────────────

function AgentCard({
  agent,
  kbsById,
  channels,
  isAdmin,
  onOpen,
  onChat,
  onArchive,
  onRestore,
}: {
  agent: AgentSummary;
  kbsById: Map<string, KbView>;
  channels: ChannelBindingSummary[];
  isAdmin: boolean;
  onOpen: () => void;
  onChat: () => void;
  onArchive: () => void;
  onRestore: () => void;
}) {
  const spec = agent.spec ?? DEFAULT_AGENT_SPEC;
  // The first non-empty line of the system prompt is what an operator
  // typically reads to decide "is this the right agent?". Falls back
  // to description so brand-new specs without a prompt still show
  // *something*.
  const blurb = useMemo(() => {
    const firstLine = (spec.system_prompt ?? "")
      .split("\n")
      .map((l) => l.trim())
      .find((l) => l.length > 0);
    const text = firstLine ?? agent.description ?? "";
    return text.slice(0, 140);
  }, [agent.description, spec.system_prompt]);

  const linkedKbNames = spec.knowledge_base_ids
    .map((id) => kbsById.get(id)?.name ?? `KB:${id.slice(0, 6)}`)
    .slice(0, 2);
  const visibleChannels = channels.slice(0, 2);
  const archived = agent.is_archived;

  return (
    <Card
      onClick={onOpen}
      className={cn(
        "group flex h-full cursor-pointer flex-col gap-3 rounded-lg border-border/70 p-3.5 shadow-none transition-all hover:border-primary/50 hover:shadow-sm",
        archived && "opacity-60",
      )}
    >
      <div className="flex items-start gap-2.5">
        <LetterAvatar seed={agent.id} label={agent.name} size="md" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span
              className={cn(
                "h-1.5 w-1.5 shrink-0 rounded-full",
                archived ? "bg-amber-500" : "bg-emerald-500",
              )}
            />
            <span className="truncate text-[13px] font-semibold text-foreground">{agent.name}</span>
            {archived ? (
              <Badge
                variant="outline"
                className="h-4 shrink-0 rounded-sm border-amber-500/40 px-1 text-[9px] font-normal uppercase tracking-wide text-amber-700 dark:text-amber-400"
              >
                Archived
              </Badge>
            ) : null}
          </div>
          <div className="truncate text-[11px] text-muted-foreground">
            {agent.description?.trim() || `id:${agent.id.slice(0, 8)}`}
          </div>
        </div>
        <div className="flex items-center opacity-0 transition-opacity group-hover:opacity-100">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="icon"
                variant="ghost"
                className="h-6 w-6 text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  onChat();
                }}
                disabled={archived}
                aria-label="Open in chat"
              >
                <MessageSquare className="h-3 w-3" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="left">Open in chat</TooltipContent>
          </Tooltip>
          {isAdmin && archived ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-muted-foreground hover:text-emerald-600"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRestore();
                  }}
                  aria-label="Restore"
                >
                  <RotateCcw className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">Restore</TooltipContent>
            </Tooltip>
          ) : isAdmin ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6 text-muted-foreground hover:text-destructive"
                  onClick={(e) => {
                    e.stopPropagation();
                    onArchive();
                  }}
                  aria-label="Archive"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">Archive</TooltipContent>
            </Tooltip>
          ) : null}
        </div>
      </div>

      <p className="line-clamp-2 min-h-[2.4em] text-[11px] leading-relaxed text-muted-foreground">
        {blurb || "—"}
      </p>

      <div className="mt-auto flex flex-wrap items-center gap-1">
        <Badge
          variant="outline"
          className="h-5 rounded-sm border-border/70 px-1.5 text-[10px] font-normal"
        >
          {spec.runtime}
        </Badge>
        <Badge
          variant="outline"
          className="h-5 rounded-sm border-border/70 px-1.5 font-mono text-[10px] font-normal"
        >
          {spec.model.model}
        </Badge>
        {visibleChannels.map((c) => (
          <Badge
            key={c.id}
            variant="lemon"
            className={cn(
              "h-5 rounded-sm px-1.5 text-[10px] font-normal",
              !c.enabled && "opacity-60",
            )}
            title={c.display_name?.trim() || c.slug}
          >
            {c.kind}
          </Badge>
        ))}
        {channels.length > visibleChannels.length ? (
          <Badge
            variant="outline"
            className="h-5 rounded-sm border-border/70 px-1.5 text-[10px] font-normal"
          >
            +{channels.length - visibleChannels.length}
          </Badge>
        ) : null}
        {linkedKbNames.length > 0 ? (
          <span className="text-[10px] text-muted-foreground">·</span>
        ) : null}
        {linkedKbNames.map((name) => (
          <Badge
            key={name}
            variant="outline"
            className="h-5 rounded-sm border-border/70 px-1.5 text-[10px] font-normal"
          >
            {name}
          </Badge>
        ))}
        {spec.knowledge_base_ids.length > linkedKbNames.length ? (
          <Badge
            variant="outline"
            className="h-5 rounded-sm border-border/70 px-1.5 text-[10px] font-normal"
          >
            +{spec.knowledge_base_ids.length - linkedKbNames.length}
          </Badge>
        ) : null}
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────
// Quick create dialog
// ────────────────────────────────────────────────────────────────────

function QuickCreateAgentDialog({
  kbs,
  onClose,
  onCreated,
}: {
  kbs: KbView[];
  onClose: () => void;
  onCreated: (agent: AgentSummary) => Promise<void> | void;
}) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [llmConfigs, setLlmConfigs] = useState<LLMConfigSummary[] | null>(null);
  const [llmConfigsError, setLlmConfigsError] = useState<string | null>(null);
  const [llmConfigId, setLlmConfigId] = useState<string | null>(null);
  const [systemPrompt, setSystemPrompt] = useState(DEFAULT_AGENT_SPEC.system_prompt);
  const [kbIds, setKbIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api<LLMConfigListResponse>("/llm/configs");
        if (!cancelled) {
          const { items, badShape } = readLlmConfigListItems(res);
          setLlmConfigs(items);
          setLlmConfigsError(
            badShape ? "Invalid response from /llm/configs — refresh or check the gateway." : null,
          );
          if (items.length > 0) {
            setLlmConfigId(items[0].id);
          }
        }
      } catch (err) {
        if (!cancelled) {
          setLlmConfigs([]);
          setLlmConfigsError(err instanceof Error ? err.message : "Failed to load team models");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedLlmConfig = useMemo(() => {
    if (!llmConfigId || !llmConfigs) return null;
    return llmConfigs.find((c) => c.id === llmConfigId) ?? null;
  }, [llmConfigId, llmConfigs]);

  const configsReady = llmConfigs !== null;
  const hasTeamModels = configsReady && llmConfigs.length > 0;

  function toggleKb(id: string): void {
    setKbIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit(): Promise<void> {
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Name is required");
      return;
    }
    if (!configsReady) {
      toast.error("Models are still loading");
      return;
    }
    if (!hasTeamModels) {
      toast.error("Add at least one model under /llm before creating an agent");
      return;
    }
    if (!selectedLlmConfig) {
      toast.error("Select a model");
      return;
    }
    setSubmitting(true);
    try {
      const spec: AgentSpec = {
        ...DEFAULT_AGENT_SPEC,
        llm_config_id: selectedLlmConfig.id,
        model: modelFromLlmConfigSummary(selectedLlmConfig, DEFAULT_AGENT_SPEC.model),
        system_prompt: systemPrompt,
        knowledge_base_ids: Array.from(kbIds),
      };
      const body: CreateAgentRequest = {
        name: trimmedName,
        description: description.trim() ? description.trim() : null,
        spec,
      };
      const created = await api<AgentSummary>("/agents", {
        method: "POST",
        body: JSON.stringify(body),
      });
      toast.success(`Created ${trimmedName}`);
      onClose();
      await onCreated(created);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to create agent");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && !submitting && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New agent</DialogTitle>
          <DialogDescription>
            Pick a name, a <span className="font-medium text-foreground">model</span> from your team
            list in <code className="text-[10px]">/llm</code>, system prompt, and optional knowledge
            bases. Each /llm row is one model (provider + API id + parameters).
          </DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <div className="space-y-3 rounded-lg border border-border/80 bg-card/40 p-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">
              Profile
            </h3>
            <div className="space-y-1.5">
              <Label htmlFor="quick-agent-name">Name</Label>
              <Input
                id="quick-agent-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Customer Support Agent"
                maxLength={200}
                required
                autoFocus
                disabled={submitting}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="quick-agent-desc">Description</Label>
              <Input
                id="quick-agent-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="One-liner about what this agent does"
                maxLength={500}
                disabled={submitting}
              />
            </div>
          </div>

          <div className="space-y-3 rounded-lg border border-border/80 bg-card/40 p-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">
              LLM spec
            </h3>
            <p className="text-[10px] leading-snug text-muted-foreground">
              In <code className="text-[10px]">/llm</code>, each row is one{" "}
              <span className="font-medium text-foreground">model</span> (slug, vendor, API model
              id, encrypted credential). You only pick which row this agent uses.
            </p>
            {hasTeamModels ? (
              <div className="space-y-1.5">
                <Label htmlFor="quick-agent-model">Model</Label>
                <Select
                  value={llmConfigId ?? llmConfigs[0].id}
                  onValueChange={(v) => setLlmConfigId(v)}
                  disabled={submitting}
                >
                  <SelectTrigger id="quick-agent-model">
                    <SelectValue placeholder="Select a model…" />
                  </SelectTrigger>
                  <SelectContent>
                    {llmConfigs?.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <span className="font-mono">{c.slug}</span>
                        {c.display_name ? (
                          <span className="ml-2 text-xs text-muted-foreground">
                            {c.display_name}
                          </span>
                        ) : null}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {llmConfigsError ? (
                  <p className="text-xs text-destructive">{llmConfigsError}</p>
                ) : null}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground">
                No model rows on <code className="text-[10px]">/llm</code> yet. Open{" "}
                <a
                  href="/llm#llm-models-card"
                  className="font-medium text-foreground underline-offset-2 hover:underline"
                >
                  /llm
                </a>{" "}
                and use <span className="font-medium">Add model</span> (vendor + key + API model id)
                first.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="quick-agent-prompt">System prompt</Label>
            <Textarea
              id="quick-agent-prompt"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
              placeholder="You are a helpful agent that…"
              rows={5}
              className="font-mono text-xs"
              disabled={submitting}
            />
          </div>
          <div className="space-y-3 rounded-lg border border-border/80 bg-card/30 p-3">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground">
              Capabilities
            </h3>
            <p className="text-[10px] leading-snug text-muted-foreground">
              Optional knowledge bases — peer to LLM fields above in the form, still stored in{" "}
              <code className="text-[10px]">spec.knowledge_base_ids</code>.
            </p>
            <fieldset className="space-y-2 rounded-lg border border-border p-3">
              <legend className="px-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                Knowledge bases
              </legend>
              {kbs.length === 0 ? (
                <p className="text-xs text-muted-foreground">
                  No knowledge bases yet. Create one in <code>/knowledge</code>, then bind it here.
                </p>
              ) : (
                <div className="grid max-h-36 grid-cols-1 gap-1 overflow-y-auto pr-1 sm:grid-cols-2">
                  {kbs.map((kb) => {
                    const checked = kbIds.has(kb.id);
                    return (
                      <label
                        key={kb.id}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-muted"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleKb(kb.id)}
                          disabled={submitting}
                        />
                        <BookOpen className="h-3 w-3 text-muted-foreground" />
                        <span className="min-w-0 flex-1 truncate font-medium">{kb.name}</span>
                        <Badge variant="outline" className="text-[10px]">
                          {kb.scope}
                        </Badge>
                      </label>
                    );
                  })}
                </div>
              )}
              {kbIds.size > 0 ? (
                <p className="text-[11px] text-muted-foreground">
                  Chat will auto-add <code>kb_search</code> for this agent and search the selected
                  KBs.
                </p>
              ) : null}
            </fieldset>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={submitting}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={submitting || !configsReady || !hasTeamModels}>
              {submitting ? <Loader2 className="animate-spin" /> : null}
              Create agent
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
